import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '@/components/Layout';
import ProductCard from '@/components/ProductCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Search, Filter, X, SlidersHorizontal, Wrench, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FilterSelect = ({ label, value, onValueChange, placeholder, options }) => (
  <div className="mb-6">
    <label className="form-label text-sm">{label}</label>
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{placeholder}</SelectItem>
        {options.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState({ styles: [], colors: [], finishes: [], categories: [] });
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [style, setStyle] = useState('');
  const [color, setColor] = useState('');
  const [finish, setFinish] = useState('');
  const [priceRange, setPriceRange] = useState([0, 1000]);
  const [showFilters, setShowFilters] = useState(false);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/filter-options`);
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      if (style) params.append('style', style);
      if (color) params.append('color', color);
      if (finish) params.append('finish', finish);
      if (priceRange[0] > 0) params.append('min_price', priceRange[0]);
      if (priceRange[1] < 1000) params.append('max_price', priceRange[1]);

      const response = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [search, category, style, color, finish, priceRange]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    const timer = setTimeout(() => fetchProducts(), 300);
    return () => clearTimeout(timer);
  }, [fetchProducts]);

  const clearFilters = () => {
    setSearch('');
    setCategory('');
    setStyle('');
    setColor('');
    setFinish('');
    setPriceRange([0, 1000]);
  };

  const hasActiveFilters = search || category || style || color || finish || priceRange[0] > 0 || priceRange[1] < 1000;

  const handleCategoryChange = (v) => setCategory(v === 'all' ? '' : v);
  const handleStyleChange = (v) => setStyle(v === 'all' ? '' : v);
  const handleColorChange = (v) => setColor(v === 'all' ? '' : v);
  const handleFinishChange = (v) => setFinish(v === 'all' ? '' : v);

  return (
    <Layout>
      <section className="bg-[#F9F7F2] py-12" data-testid="shop-header">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="font-manrope text-4xl font-bold text-[#1A1A1A] mb-4">Shop Kitchen Cabinets</h1>
          <p className="text-[#6B7280] max-w-2xl">Browse our collection of premium kitchen cabinets.</p>
        </div>
      </section>

      {/* Custom Cabinet Builder CTA */}
      <section className="max-w-7xl mx-auto px-6 py-8" data-testid="custom-cabinet-cta">
        <Link to="/cabinets" className="block">
          <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#1A1A1A] to-[#374151] p-6 md:p-8 transition-transform hover:scale-[1.01]">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-[#8B5E3C] flex items-center justify-center flex-shrink-0">
                  <Wrench size={28} className="text-white" />
                </div>
                <div>
                  <h3 className="font-manrope text-xl md:text-2xl font-bold text-white mb-1">Custom Cabinet Builder</h3>
                  <p className="text-gray-300 text-sm md:text-base">Design your perfect cabinets with our interactive configurator. Choose type, size, materials & more.</p>
                </div>
              </div>
              <Button className="bg-[#8B5E3C] hover:bg-[#7a5235] text-white px-6 whitespace-nowrap" data-testid="custom-cabinet-btn">
                Build Custom <ChevronRight size={18} className="ml-1" />
              </Button>
            </div>
            <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-[#8B5E3C]/10 rounded-full" />
            <div className="absolute -right-5 -top-5 w-20 h-20 bg-[#8B5E3C]/10 rounded-full" />
          </div>
        </Link>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Desktop Filters */}
          <aside className="hidden lg:block w-72 flex-shrink-0" data-testid="filters-sidebar-desktop">
            <div className="filter-sidebar sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-manrope font-semibold text-lg text-[#1A1A1A]">Filters</h2>
                {hasActiveFilters && (
                  <button onClick={clearFilters} className="text-sm text-[#8B5E3C] hover:underline" data-testid="clear-filters-btn">Clear All</button>
                )}
              </div>

              <div className="mb-6">
                <label className="form-label text-sm">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" size={18} />
                  <Input type="text" placeholder="Search cabinets..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="search-input" />
                </div>
              </div>

              <FilterSelect label="Category" value={category} onValueChange={handleCategoryChange} placeholder="All Categories" options={filterOptions.categories} />
              <FilterSelect label="Style" value={style} onValueChange={handleStyleChange} placeholder="All Styles" options={filterOptions.styles} />
              <FilterSelect label="Color" value={color} onValueChange={handleColorChange} placeholder="All Colors" options={filterOptions.colors} />
              <FilterSelect label="Finish" value={finish} onValueChange={handleFinishChange} placeholder="All Finishes" options={filterOptions.finishes} />

              <div className="mb-6">
                <label className="form-label text-sm">Price Range</label>
                <div className="px-2 pt-4">
                  <Slider value={priceRange} min={0} max={1000} step={10} onValueChange={setPriceRange} data-testid="price-slider" />
                  <div className="flex justify-between mt-2 text-sm text-[#6B7280]">
                    <span>${priceRange[0]}</span>
                    <span>${priceRange[1]}</span>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          {/* Mobile Filters Toggle */}
          <div className="lg:hidden flex items-center justify-between mb-4">
            <Button variant="outline" onClick={() => setShowFilters(true)} className="flex items-center gap-2" data-testid="mobile-filters-btn">
              <SlidersHorizontal size={18} />Filters
              {hasActiveFilters && <span className="bg-[#8B5E3C] text-white text-xs px-2 py-0.5 rounded-full">Active</span>}
            </Button>
            <span className="text-sm text-[#6B7280]">{products.length} products</span>
          </div>

          {/* Mobile Filters Modal */}
          {showFilters && (
            <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-filters-modal">
              <div className="absolute inset-0 bg-black/50" onClick={() => setShowFilters(false)} />
              <div className="absolute right-0 top-0 h-full w-full max-w-sm bg-white overflow-y-auto">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-manrope font-semibold text-lg">Filters</h2>
                    <button onClick={() => setShowFilters(false)}><X size={24} /></button>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="form-label text-sm">Search</label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" size={18} />
                        <Input type="text" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
                      </div>
                    </div>
                    <FilterSelect label="Category" value={category} onValueChange={handleCategoryChange} placeholder="All Categories" options={filterOptions.categories} />
                    <FilterSelect label="Style" value={style} onValueChange={handleStyleChange} placeholder="All Styles" options={filterOptions.styles} />
                    <FilterSelect label="Color" value={color} onValueChange={handleColorChange} placeholder="All Colors" options={filterOptions.colors} />
                    <FilterSelect label="Finish" value={finish} onValueChange={handleFinishChange} placeholder="All Finishes" options={filterOptions.finishes} />
                  </div>
                  <div className="flex gap-4 mt-8">
                    <Button variant="outline" className="flex-1" onClick={clearFilters}>Clear All</Button>
                    <Button className="flex-1 bg-[#1A1A1A]" onClick={() => setShowFilters(false)}>Apply</Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Products Grid */}
          <div className="flex-1">
            <div className="hidden lg:flex items-center justify-between mb-6">
              <span className="text-sm text-[#6B7280]">{products.length} products found</span>
            </div>

            {loading ? (
              <div className="flex justify-center py-20"><div className="spinner" /></div>
            ) : products.length === 0 ? (
              <div className="text-center py-20" data-testid="no-products">
                <Filter size={48} className="mx-auto text-[#E5E7EB] mb-4" />
                <h3 className="font-manrope text-xl font-semibold text-[#1A1A1A] mb-2">No products found</h3>
                <p className="text-[#6B7280] mb-6">Try adjusting your filters</p>
                <Button onClick={clearFilters}>Clear Filters</Button>
              </div>
            ) : (
              <div className="product-grid" data-testid="products-grid">
                {products.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
